import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useRef } from 'react';
import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';

import type { ManualEditStore } from './manual-edit-store';
import { createManualEditStore } from './manual-edit-store';

const ManualEditStoreContext = createContext<StoreApi<ManualEditStore> | null>(null);

type ManualEditStoreProviderProps = PropsWithChildren<{
    readonly initialStore?: StoreApi<ManualEditStore>;
}>;

export const ManualEditStoreProvider: FunctionComponent<ManualEditStoreProviderProps> = ({
    children,
    initialStore,
}) => {
    const ancestorStore = useContext(ManualEditStoreContext);
    const storeRef = useRef<StoreApi<ManualEditStore> | null>(null);
    storeRef.current ??= initialStore ?? ancestorStore ?? createManualEditStore();

    return <ManualEditStoreContext.Provider value={storeRef.current}>{children}</ManualEditStoreContext.Provider>;
};

export const useManualEditStore = <T,>(selector: (state: ManualEditStore) => T): T => {
    const store = useContext(ManualEditStoreContext);

    if (store === null) {
        throw new Error('useManualEditStore must be used within ManualEditStoreProvider');
    }

    return useStore(store, selector);
};
